import useDiscoveryFetch from './useDiscoveryFetch';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Form, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryCityCard from './MobileDiscoveryCityCard';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import MobileAppliedChips from './MobileAppliedChips';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import CountryDetailMobileTabs from './countryDetail/CountryDetailMobileTabs';
import getCountryDetailNavItems from './countryDetail/countryDetailNavItems';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icJobSearch from '../../assets/img/ic-job-search.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { MAPBOX_TOKEN } from '../../config/mapbox';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { mobileCitySortDefinitions, getMobileSortedCities } from './mobileCitySort';
import { getCityCoords } from './cityMapCoords';

const PAGE_DISCOVERY_TYPE = 'cities';

// Country is redundant here (every row already belongs to this route's
// country) and Continent is redundant too, since a country maps to exactly
// one continent — the standalone Cities sort/filter options for those are
// intentionally omitted on this route-scoped page.
const mobileSortOptions = [
    { field: 'cityName', label: 'City name' },
    { field: 'population', label: 'Population' },
];

const DiscoveryCountryCityList = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [countryName, setCountryName] = useState('');
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [citiesList, setCitiesList] = useState([]);
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderComments, setSortOrderComments] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [citiesPerPage, setCitiesPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [selectedLanguagesSpoken, setSelectedLanguagesSpoken] = useState([]);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    const [favorites, setFavorites] = useState(new Set());
    const [geocodedCoords, setGeocodedCoords] = useState({});

    const languagesSpokenOptions = [
        ...new Set(
            citiesList.flatMap((cityItem) =>
                Array.isArray(cityItem.languagesSpoken)
                    ? cityItem.languagesSpoken
                        .map((language) => String(language || '').trim())
                        .filter(Boolean)
                    : []
            )
        ),
    ]
        .sort((a, b) => a.localeCompare(b))
        .map((language) => ({
            value: language,
            label: language,
        }));

    const filteredCities = citiesList.filter((cityItem) => {
        const searchText = [
            cityItem.cityId,
            cityItem.name,
            cityItem.cityName,
            cityItem.country?.name,
            cityItem.continent,
            cityItem.comments,
            Array.isArray(cityItem.languagesSpoken) ? cityItem.languagesSpoken.join(' ') : '',
        ]
            .join(' ')
            .toLowerCase();

        const matchesSearch =
            !searchQuery || searchText.includes(searchQuery.toLowerCase());

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(cityItem._id));

        const matchesLanguagesSpoken =
            !Array.isArray(selectedLanguagesSpoken) ||
            selectedLanguagesSpoken.length === 0 ||
            (
                Array.isArray(cityItem.languagesSpoken) &&
                selectedLanguagesSpoken.some((selectedLanguage) =>
                    cityItem.languagesSpoken.includes(selectedLanguage)
                )
            );

        return (
            matchesSearch &&
            matchesFavorites &&
            matchesLanguagesSpoken
        );
    });

    const citiesWithCoordinates = useMemo(
        () => filteredCities.filter((cityItem) => getCityCoords(cityItem, geocodedCoords) !== null),
        [filteredCities, geocodedCoords]
    );

    const anyCitiesWithCoordinates = useMemo(
        () => citiesList.some((cityItem) => getCityCoords(cityItem, geocodedCoords) !== null),
        [citiesList, geocodedCoords]
    );

    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);

    const selectedLanguagesSpokenKey = useMemo(
        () => selectedLanguagesSpoken.join('|'),
        [selectedLanguagesSpoken]
    );

    const mobileSortedCities = sortField
        ? getMobileSortedCities(filteredCities, sortField, sortDirection)
        : filteredCities;

    // Route country (id) is part of the signature so navigating between
    // Country routes resets Load More back to the first page instead of
    // carrying over a stale visibleCount from the previous country.
    const mobileFilterKey = JSON.stringify({
        id,
        searchQuery,
        selectedFavorites,
        selectedLanguagesSpoken,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: mobileSortedCities,
        filterKey: mobileFilterKey,
        storageKey: 'discovery-country-cities-mobile-list',
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedFavorites,
        selectedLanguagesSpokenKey,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedFavorites,
        selectedLanguagesSpoken,
    });

    // Country is fixed by the route, not a filter, so a saved filter set for
    // this page never carries a country value to apply — and even a legacy
    // saved set that happened to have one is ignored here rather than
    // allowed to override the route's country.
    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
        setSelectedLanguagesSpoken(
            Array.isArray(filters.selectedLanguagesSpoken)
                ? filters.selectedLanguagesSpoken
                : []
        );
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;

            const payload = {
                page: 'country-cities',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };

            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;

            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );
                return [savedFilterSet, ...withoutDuplicate];
            });

            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery country city filter set:', error);
        }
    };

    // preserveSearch: mobile deletes a saved preset without touching the live
    // toolbar search, since Search is independent of staged/saved filter state there.
    // Desktop keeps the previous full-reset behavior.
    const handleDeleteFilterSet = async (filterSetId, { preserveSearch = false } = {}) => {
        if (!filterSetId || filterSetId === 'default') return;

        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);

            const wasSelected = String(selectedFilterSet) === String(filterSetId);

            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );

            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet(preserveSearch ? { searchQuery } : {});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);

        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }

        const selectedSavedFilterSet = savedFilterSets.find(
            (item) => item._id === value
        );

        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchData = async () => {
            try {
                const [countryRes, citiesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get(`/api/discovery/countries/${id}`, { signal }),
                    api.get(`/api/discovery/countries/${id}/cities`, { signal }),
                    api.get('/api/discovery-filter-sets?page=country-cities', { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const countryData = countryRes.data;
                setCountryName(countryData?.countryName || countryData?.country?.name || id);

                const safeCities = Array.isArray(citiesRes.data) ? citiesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCities = Array.isArray(currentUserRes?.data?.favoriteCities)
                    ? currentUserRes.data.favoriteCities.map((cityId) => String(cityId))
                    : [];

                setCitiesList(refreshSortRef.current ? [...safeCities].sort(refreshSortRef.current) : safeCities);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCities));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching country cities / filter sets / favorites:', error);
            }
        };

        fetchData();
    }, [id]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!citiesList.length || !MAPBOX_TOKEN) return;

        const needsGeocoding = citiesList.filter((city) => {
            const latNum = Number(city.latitude);
            const lngNum = Number(city.longitude);
            return city.latitude == null || city.longitude == null ||
                Number.isNaN(latNum) || Number.isNaN(lngNum) ||
                (latNum === 0 && lngNum === 0);
        });

        if (!needsGeocoding.length) return;

        needsGeocoding.forEach(async (city) => {
            const cityId = String(city._id);
            const cityName = String(city.name || city.cityName || '').trim();
            const cityCountryName = String(city.country?.name || city.countryName || '').trim();
            if (!cityName || !cityCountryName) return;

            try {
                const query = encodeURIComponent(`${cityName}, ${cityCountryName}`);
                const res = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=place&limit=1&access_token=${MAPBOX_TOKEN}`, { signal }
                );
                if (!isCurrent()) return;
                const data = await res.json();
                if (!isCurrent()) return;
                const center = data?.features?.[0]?.center;
                if (Array.isArray(center) && center.length >= 2) {
                    const [geocodedLng, geocodedLat] = center;
                    setGeocodedCoords((prev) => ({
                        ...prev,
                        [cityId]: { lat: geocodedLat, lng: geocodedLng },
                    }));
                }
            } catch {
                if (!isCurrent()) return;
                // silently ignore geocoding failures
            }
        });
    }, [citiesList], { reconnect: false });

    const sortCitiesByName = () => {
        const newSortOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const cityA = String(a.name || a.cityName || '').toLowerCase();
            const cityB = String(b.name || b.cityName || '').toLowerCase();

            return newSortOrder === 'asc'
                ? cityA.localeCompare(cityB)
                : cityB.localeCompare(cityA);
        };
        const sortedCities = [...citiesList].sort(refreshSortRef.current);

        setCitiesList(sortedCities);
        setSortOrderCity(newSortOrder);
    };

    const sortCitiesByComments = () => {
        const newSortOrder = sortOrderComments === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const commentsA = Number(a.comments) || 0;
            const commentsB = Number(b.comments) || 0;

            return newSortOrder === 'asc'
                ? commentsA - commentsB
                : commentsB - commentsA;
        };
        const sortedCities = [...citiesList].sort(refreshSortRef.current);

        setCitiesList(sortedCities);
        setSortOrderComments(newSortOrder);
    };

    const totalPages = Math.ceil(filteredCities.length / citiesPerPage);
    const noCities = filteredCities.length === 0;

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;

        if (totalPages <= 1) return [1];

        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };

        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(
            Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
            totalPages
        );

        const siblingsStart = Math.max(
            Math.min(
                currentPage - siblingCount,
                totalPages - boundaryCount - siblingCount * 2 - 1
            ),
            boundaryCount + 2
        );

        const siblingsEnd = Math.min(
            Math.max(
                currentPage + siblingCount,
                boundaryCount + siblingCount * 2 + 2
            ),
            totalPages - boundaryCount - 1
        );

        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2
                ? ['ellipsis']
                : boundaryCount + 1 < totalPages - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1
                ? ['ellipsis']
                : totalPages - boundaryCount > boundaryCount
                    ? [totalPages - boundaryCount]
                    : []),
            ...endPages,
        ];

        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (cityId) => {
        const cityIdStr = String(cityId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(cityIdStr);

        const optimisticFavorites = new Set(previousFavorites);

        if (wasFavorited) {
            optimisticFavorites.delete(cityIdStr);
        } else {
            optimisticFavorites.add(cityIdStr);
        }

        setFavorites(optimisticFavorites);

        try {
            const res = await api.patch(`/api/users/me/favorite-cities/${cityIdStr}`);

            const safeFavoriteCities = Array.isArray(res?.data?.favoriteCities)
                ? res.data.favoriteCities.map((favId) => String(favId))
                : [];

            setFavorites(new Set(safeFavoriteCities));
        } catch (error) {
            console.error('Error toggling favorite city:', error);
            setFavorites(previousFavorites);
        }
    };

    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);

    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };

    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };

    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };

    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };

    const selectDraftSort = (field) => {
        setDraftSortDirection((currentDirection) => (
            draftSortField === field
                ? (currentDirection === 'asc' ? 'desc' : 'asc')
                : mobileCitySortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };

    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };

    // Route country is not part of local filter state, so clearing filters
    // here can never remove it — only search/favorites/languages/sort reset.
    const clearMobileFiltersPreserveSearch = () => {
        setSelectedFavorites(null);
        setSelectedLanguagesSpoken([]);
    };

    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
    };

    const appliedMobileChips = [];

    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc'
                ? <FaCaretUp aria-hidden="true" />
                : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    // This list has no company/industry/job data of its own — those are company
    // attributes. Route those to Companies instead of pretending this page can filter
    // on them. country/city/language are all supported locally via search + language.
    const isCompanySpecificFilter = (sf) =>
        Boolean(sf?.industry) ||
        sf?.jobType === 'student' ||
        sf?.jobType === 'english-speaking' ||
        sf?.jobType === 'internship';

    // Supported locally: country/city -> searchQuery, language -> selectedLanguagesSpoken.
    const applyStructuredFilters = (sf) => {
        if (!sf) return;

        if (sf.city) {
            setSearchQuery(sf.city);
        } else if (sf.country) {
            setSearchQuery(sf.country);
        }

        if (sf.language) {
            setSelectedLanguagesSpoken((prev) =>
                prev.includes(sf.language) ? prev : [...prev, sf.language]
            );
        }
    };

    // Already scoped to this country (id is its ISO2 code), so a company-specific chip
    // navigates to that same country's context in Companies rather than the global list.
    const navigateToCompaniesWithFilters = (sf) => {
        const params = new URLSearchParams();

        params.set('country', id);
        if (sf.city) params.set('city', sf.city);
        if (sf.industry) params.set('industry', sf.industry);
        if (sf.jobType === 'student') params.set('studentFriendly', 'true');
        if (sf.jobType === 'internship') params.set('internship', 'true');
        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            params.set('englishSpeaking', 'true');
        }

        navigate(`/discovery/companies?${params.toString()}`);
    };

    const handleChipClick = (sf) => {
        if (!sf) return;

        if (isCompanySpecificFilter(sf)) {
            navigateToCompaniesWithFilters(sf);
            return;
        }

        applyStructuredFilters(sf);
    };

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext(
        {
            pageType: 'discovery-country-cities',
            mapFirst: true,
            mapScope: 'scoped',
            discoveryType: PAGE_DISCOVERY_TYPE,
            countryId: id,
            countryName,
            activeTab: 'Cities',
            suggestions: getDiscoveryAiSuggestions({
                discoveryType: PAGE_DISCOVERY_TYPE,
                selectedCountry: countryName,
            }),
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    const countryNavItems = getCountryDetailNavItems(id);

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-cities-page discovery-map-page${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb countryName={countryName} subPage="Cities" />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>{countryName ? `${countryName}` : 'Cities'}</h1>
                                        </Col>
                                    </Row>

                                    {anyCitiesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={citiesWithCoordinates}
                                                        height={260}
                                                        singleItemZoom={6}
                                                        compactSpreadZoom={true}
                                                        groupClickZoom={7}
                                                        spreadZoomOffset={0}
                                                        clusterPixelRadius={30}
                                                        markerScale={0.6}
                                                        markerClassName="discovery-map-marker--city"
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(cityItem) => getCityCoords(cityItem, geocodedCoords)?.lat}
                                                        getLongitude={(cityItem) => getCityCoords(cityItem, geocodedCoords)?.lng}
                                                        getTitle={(cityItem) => cityItem.name || cityItem.cityName || 'City'}
                                                        getSubtitle={(cityItem) => cityItem.country?.name || cityItem.countryName || ''}
                                                        getLink={(cityItem) => `/discovery/cities/${cityItem._id}`}
                                                        ctaLabel="Explore city →"
                                                        getSecondaryLink={() => `/discovery/countries/${id}/companies`}
                                                        secondaryCtaLabel="View companies →"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile ? (
                                        <CountryDetailMobileTabs items={countryNavItems} mapPage />
                                    ) : (
                                        <Row className="mb-4">
                                            <Col>
                                                <NavTabs items={countryNavItems} />
                                            </Col>
                                        </Row>
                                    )}

                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Cities"
                                                    variant="countries"
                                                    items={citiesList}
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    showCountryFilter={false}
                                                    showContinentAllocation={false}
                                                    selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                    setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                    languagesSpokenOptions={languagesSpokenOptions}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryHeading title="Cities" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search cities"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                    {
                                                        label: 'Sort cities',
                                                        icon: icSort,
                                                        onClick: openMobileSort,
                                                    },
                                                ]}
                                            />

                                            <MobileAppliedChips chips={appliedMobileChips} />

                                            <DiscoveryFilterBar
                                                title="City"
                                                variant="countries"
                                                items={citiesList}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                selectedFavorites={selectedFavorites}
                                                setSelectedFavorites={setSelectedFavorites}
                                                showCountryFilter={false}
                                                showContinentAllocation={false}
                                                selectedLanguagesSpoken={selectedLanguagesSpoken}
                                                setSelectedLanguagesSpoken={setSelectedLanguagesSpoken}
                                                languagesSpokenOptions={languagesSpokenOptions}
                                                savedFilterSets={savedFilterSets}
                                                selectedFilterSet={selectedFilterSet}
                                                onFilterSetChange={handleFilterSetChange}
                                                onDeleteFilterSet={(filterSetId) => handleDeleteFilterSet(filterSetId, { preserveSearch: true })}
                                                onSaveFilters={handleSaveFilters}
                                                onClearFilters={clearMobileFiltersPreserveSearch}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={() => setShowMobileFilters(false)}
                                                mobileModalTitle="Filter cities"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {noCities && isMobile ? (
                                        <DiscoveryEmptyState
                                            className="discovery-empty-state--mobile-jobs"
                                            icon={icJobSearch}
                                            title="No matching cities found"
                                            description="Try removing one or more filters or adjust your search."
                                            primaryAction={{
                                                label: 'Clear search & filters',
                                                variant: 'primary',
                                                onClick: clearEmptyStateSearchAndFilters,
                                            }}
                                            secondaryAction={{
                                                label: 'Adjust filters',
                                                variant: 'outline-secondary',
                                                onClick: () => setShowMobileFilters(true),
                                            }}
                                        />
                                    ) : noCities ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No cities available.</p>
                                            </Col>
                                        </Row>
                                    ) : isMobile ? (
                                        <div className="mobile-company-list mobile-company-list--country-detail">
                                            {mobilePagination.mobileItems.map((cityItem) => (
                                                <MobileDiscoveryCityCard
                                                    key={cityItem._id}
                                                    city={cityItem}
                                                    isFavorite={favorites.has(String(cityItem._id))}
                                                    onFavoriteToggle={toggleFavorite}
                                                    onOpenCity={(selectedCity) => {
                                                        mobilePagination.rememberScrollPosition();
                                                        navigate(`/discovery/countries/${id}/cities/${selectedCity._id}`);
                                                    }}
                                                />
                                            ))}
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="cities"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table discovery-table--cities">
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>

                                                                <th onClick={sortCitiesByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        City Region
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th>Comments</th>

                                                                <th>Favorite</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCities.map((cityItem) => (
                                                                <tr
                                                                    className="company-row discovery-row"
                                                                    key={cityItem._id}
                                                                    onClick={() => navigate(`/discovery/countries/${id}/cities/${cityItem._id}`)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <td>{cityItem.cityId || '—'}</td>
                                                                    <td>{cityItem.name || cityItem.cityName || '—'}</td>
                                                                    <td>{cityItem.comments ?? 0}</td>
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(cityItem._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(cityItem._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && (
                                        <Row className="pagination-wrapper">
                                            <Col className="d-flex align-items-center">
                                                <span>
                                                    Total {filteredCities.length} {filteredCities.length === 1 ? 'city' : 'cities'}
                                                </span>
                                                <Pagination>
                                                    <Pagination.Prev
                                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        <BsChevronLeft />
                                                    </Pagination.Prev>

                                                    {getPaginationItems().map((item, index) =>
                                                        item === 'ellipsis' ? (
                                                            <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                        ) : (
                                                            <Pagination.Item
                                                                key={item}
                                                                active={item === currentPage}
                                                                onClick={() => handlePageChange(item)}
                                                            >
                                                                {item}
                                                            </Pagination.Item>
                                                        )
                                                    )}

                                                    <Pagination.Next
                                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages || totalPages === 0}
                                                    >
                                                        <BsChevronRight />
                                                    </Pagination.Next>
                                                </Pagination>
                                            </Col>

                                            <Col className="pagination-controls">
                                                <div className="pagination-controls-per-page">
                                                    <Form.Select
                                                        value={citiesPerPage}
                                                        onChange={(e) => {
                                                            setCitiesPerPage(Number(e.target.value));
                                                            setCurrentPage(1);
                                                        }}
                                                    >
                                                        <option value="10">10 / page</option>
                                                        <option value="20">20 / page</option>
                                                        <option value="40">40 / page</option>
                                                    </Form.Select>
                                                </div>

                                                <label className="pagination-controls-goto-label" htmlFor="goto-page-country-cities">
                                                    Go to
                                                </label>

                                                <Form.Control
                                                    id="goto-page-country-cities"
                                                    className="pagination-controls-goto-input"
                                                    type="number"
                                                    value={currentPage}
                                                    onChange={(e) => {
                                                        const newPage = Number(e.target.value);
                                                        if (newPage >= 1 && newPage <= totalPages) {
                                                            setCurrentPage(newPage);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        if (currentPage < 1) setCurrentPage(1);
                                                        if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal
                className="with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen discovery-sort-mobile-modal"
                show={showMobileSort}
                onHide={closeMobileSort}
            >
                <div className="discovery-filter-mobile-panel">
                    <div className="discovery-filter-mobile-panel__header">
                        <Modal.Title>Sort cities</Modal.Title>
                        <div className="discovery-filter-mobile-panel__header-actions">
                            <button type="button" className="discovery-filter-mobile-panel__close" onClick={closeMobileSort} aria-label="Close sort cities">
                                &times;
                            </button>
                        </div>
                    </div>

                    <Modal.Body>
                        <div className="discovery-sort-mobile-options" role="radiogroup" aria-label="Sort cities by">
                            {mobileSortOptions.map((option) => {
                                const isActive = draftSortField === option.field;
                                return (
                                    <button
                                        key={option.field}
                                        type="button"
                                        role="radio"
                                        className={`discovery-sort-mobile-option${isActive ? ' is-active' : ''}`}
                                        onClick={() => selectDraftSort(option.field)}
                                        aria-checked={isActive}
                                    >
                                        <span className="discovery-sort-mobile-option__label">
                                            <span className="discovery-sort-mobile-option__radio" aria-hidden="true" />
                                            <span>{option.label}</span>
                                        </span>
                                        {isActive && (
                                            <span className="discovery-sort-mobile-option__direction" aria-label={draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                                                <span>{draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}</span>
                                                {draftSortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </Modal.Body>

                    <div className="discovery-filter-mobile-panel__footer">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            className="discovery-filter-mobile-panel__reset"
                            onClick={clearMobileSortFromDrawer}
                        >
                            Clear sort
                        </Button>

                        <Button
                            type="button"
                            variant="primary"
                            className="discovery-filter-mobile-panel__apply"
                            onClick={applyMobileSort}
                        >
                            Apply sort
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DiscoveryCountryCityList;
